/**
 * Doc-hash integrity engine — REAL SHA-256 over canonical document bytes.
 *
 * Fixes the auditor's finding that the signature doc-hash was weak/absent:
 * this module binds a signature to the EXACT rendered document with a
 * cryptographic digest, not a 32-bit checksum.
 *
 *   • {@link canonicalizeDoc} — deterministic, key-sorted serialization of
 *     a proposal document's blocks (+ brand kit). Key insertion order can
 *     never change the bytes, and volatile bookkeeping (versionSeq, ids)
 *     is deliberately excluded so only SUBSTANCE moves the hash.
 *   • {@link sha256Hex} — SHA-256 via WebCrypto (`crypto.subtle`) when
 *     available, with a pure, dependency-free FIPS 180-4 implementation as
 *     the deterministic fallback ({@link sha256HexSync}) so hashing works
 *     identically in the browser, in workers, and in the node test env.
 *     (Implemented from the published spec — no copyleft code borrowed.)
 *   • {@link computeDocHash} — the one call sites use: canonical bytes in,
 *     64-char lowercase hex out.
 *   • {@link shortHash} — the 8-hex-char human fingerprint for UI chips.
 *
 * Pure by design: no store, no fetch, no ambient state.
 */

// ---------------------------------------------------------------------------
// Canonicalization
// ---------------------------------------------------------------------------

/**
 * The minimal shape this engine hashes — structurally compatible with
 * `proposalBlocks.ts`'s ProposalDocModel (blocks + brandKit) without
 * coupling to it, so any block-shaped document can be sealed.
 */
export interface SignableDoc {
  blocks: unknown[];
  brandKit?: unknown;
}

/**
 * Deterministic JSON with sorted object keys and undefined values dropped —
 * the same stable-stringify convention proposalSignature/proposalBlocks use,
 * so reordering keys or adding `undefined` noise can never move a hash.
 */
function stableStringify(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) {
    return '[' + value.map((v) => stableStringify(v)).join(',') + ']';
  }
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort();
  return (
    '{' +
    keys.map((k) => JSON.stringify(k) + ':' + stableStringify(obj[k])).join(',') +
    '}'
  );
}

/**
 * Canonical bytes for a document: the substance (blocks + brand kit),
 * stable-stringified. Excludes ids/version counters on purpose — a
 * re-snapshot with identical content must hash identically.
 */
export function canonicalizeDoc(doc: SignableDoc): string {
  return stableStringify({
    blocks: doc.blocks,
    brandKit: doc.brandKit ?? null,
  });
}

// ---------------------------------------------------------------------------
// SHA-256 — WebCrypto fast path + pure deterministic fallback
// ---------------------------------------------------------------------------

/** UTF-8 encode without assuming TextEncoder exists in every runtime. */
function utf8Bytes(s: string): Uint8Array {
  if (typeof TextEncoder !== 'undefined') return new TextEncoder().encode(s);
  const out: number[] = [];
  for (const ch of s) {
    const cp = ch.codePointAt(0)!;
    if (cp < 0x80) out.push(cp);
    else if (cp < 0x800) out.push(0xc0 | (cp >> 6), 0x80 | (cp & 0x3f));
    else if (cp < 0x10000)
      out.push(0xe0 | (cp >> 12), 0x80 | ((cp >> 6) & 0x3f), 0x80 | (cp & 0x3f));
    else
      out.push(
        0xf0 | (cp >> 18),
        0x80 | ((cp >> 12) & 0x3f),
        0x80 | ((cp >> 6) & 0x3f),
        0x80 | (cp & 0x3f),
      );
  }
  return Uint8Array.from(out);
}

function toHex(bytes: Uint8Array): string {
  let out = '';
  for (let i = 0; i < bytes.length; i++) {
    out += bytes[i].toString(16).padStart(2, '0');
  }
  return out;
}

// FIPS 180-4 round constants (first 32 bits of the fractional parts of the
// cube roots of the first 64 primes).
const K = new Uint32Array([
  0x428a2f98, 0x71374491, 0xb5c0fbcf, 0xe9b5dba5, 0x3956c25b, 0x59f111f1,
  0x923f82a4, 0xab1c5ed5, 0xd807aa98, 0x12835b01, 0x243185be, 0x550c7dc3,
  0x72be5d74, 0x80deb1fe, 0x9bdc06a7, 0xc19bf174, 0xe49b69c1, 0xefbe4786,
  0x0fc19dc6, 0x240ca1cc, 0x2de92c6f, 0x4a7484aa, 0x5cb0a9dc, 0x76f988da,
  0x983e5152, 0xa831c66d, 0xb00327c8, 0xbf597fc7, 0xc6e00bf3, 0xd5a79147,
  0x06ca6351, 0x14292967, 0x27b70a85, 0x2e1b2138, 0x4d2c6dfc, 0x53380d13,
  0x650a7354, 0x766a0abb, 0x81c2c92e, 0x92722c85, 0xa2bfe8a1, 0xa81a664b,
  0xc24b8b70, 0xc76c51a3, 0xd192e819, 0xd6990624, 0xf40e3585, 0x106aa070,
  0x19a4c116, 0x1e376c08, 0x2748774c, 0x34b0bcb5, 0x391c0cb3, 0x4ed8aa4a,
  0x5b9cca4f, 0x682e6ff3, 0x748f82ee, 0x78a5636f, 0x84c87814, 0x8cc70208,
  0x90befffa, 0xa4506ceb, 0xbef9a3f7, 0xc67178f2,
]);

const rotr = (x: number, n: number): number => (x >>> n) | (x << (32 - n));

/**
 * Pure synchronous SHA-256 → lowercase hex. Deterministic everywhere; used
 * as the fallback when WebCrypto is unavailable (and directly testable so
 * the fallback path is proven, not assumed).
 */
export function sha256HexSync(bytes: Uint8Array | string): string {
  const data = typeof bytes === 'string' ? utf8Bytes(bytes) : bytes;
  const len = data.length;

  // Pad: message ∥ 0x80 ∥ zeros ∥ 64-bit big-endian bit length.
  const paddedLen = (((len + 8) >> 6) << 6) + 64;
  const padded = new Uint8Array(paddedLen);
  padded.set(data);
  padded[len] = 0x80;
  const dv = new DataView(padded.buffer);
  dv.setUint32(paddedLen - 8, Math.floor(len / 0x20000000), false);
  dv.setUint32(paddedLen - 4, (len << 3) >>> 0, false);

  const H = new Uint32Array([
    0x6a09e667, 0xbb67ae85, 0x3c6ef372, 0xa54ff53a, 0x510e527f, 0x9b05688c,
    0x1f83d9ab, 0x5be0cd19,
  ]);
  const w = new Uint32Array(64);

  for (let off = 0; off < paddedLen; off += 64) {
    for (let i = 0; i < 16; i++) w[i] = dv.getUint32(off + i * 4, false);
    for (let i = 16; i < 64; i++) {
      const s0 = rotr(w[i - 15], 7) ^ rotr(w[i - 15], 18) ^ (w[i - 15] >>> 3);
      const s1 = rotr(w[i - 2], 17) ^ rotr(w[i - 2], 19) ^ (w[i - 2] >>> 10);
      w[i] = (w[i - 16] + s0 + w[i - 7] + s1) >>> 0;
    }
    let a = H[0], b = H[1], c = H[2], d = H[3];
    let e = H[4], f = H[5], g = H[6], h = H[7];
    for (let i = 0; i < 64; i++) {
      const S1 = rotr(e, 6) ^ rotr(e, 11) ^ rotr(e, 25);
      const ch = (e & f) ^ (~e & g);
      const t1 = (h + S1 + ch + K[i] + w[i]) >>> 0;
      const S0 = rotr(a, 2) ^ rotr(a, 13) ^ rotr(a, 22);
      const maj = (a & b) ^ (a & c) ^ (b & c);
      const t2 = (S0 + maj) >>> 0;
      h = g; g = f; f = e; e = (d + t1) >>> 0;
      d = c; c = b; b = a; a = (t1 + t2) >>> 0;
    }
    H[0] = (H[0] + a) >>> 0; H[1] = (H[1] + b) >>> 0;
    H[2] = (H[2] + c) >>> 0; H[3] = (H[3] + d) >>> 0;
    H[4] = (H[4] + e) >>> 0; H[5] = (H[5] + f) >>> 0;
    H[6] = (H[6] + g) >>> 0; H[7] = (H[7] + h) >>> 0;
  }

  const out = new Uint8Array(32);
  const outDv = new DataView(out.buffer);
  for (let i = 0; i < 8; i++) outDv.setUint32(i * 4, H[i], false);
  return toHex(out);
}

/**
 * SHA-256 → lowercase hex. Prefers WebCrypto's native, audited
 * implementation; falls back to {@link sha256HexSync} when `crypto.subtle`
 * is unavailable (or errors), so the result is deterministic in EVERY env.
 */
export async function sha256Hex(bytes: Uint8Array | string): Promise<string> {
  const data = typeof bytes === 'string' ? utf8Bytes(bytes) : bytes;
  const subtle = (globalThis as { crypto?: Crypto }).crypto?.subtle;
  if (subtle) {
    try {
      // Copy into a fresh ArrayBuffer so typed-array views over shared or
      // offset buffers can never leak neighbouring bytes into the digest.
      const digest = await subtle.digest('SHA-256', data.slice().buffer);
      return toHex(new Uint8Array(digest));
    } catch {
      // Fall through to the pure implementation.
    }
  }
  return sha256HexSync(data);
}

// ---------------------------------------------------------------------------
// The doc-hash call sites use
// ---------------------------------------------------------------------------

/** SHA-256 hex over the document's canonical bytes. */
export async function computeDocHash(doc: SignableDoc): Promise<string> {
  return sha256Hex(canonicalizeDoc(doc));
}

/** The human-readable fingerprint: first 8 hex chars of a digest. */
export function shortHash(hex: string): string {
  return hex.slice(0, 8);
}
